import { Controller } from "@hotwired/stimulus"
import { LOCATION_KEY, hasFreshFix, isDeniedLocally } from "../field_location"

const SAPPHIRE     = "#003d68"
const AMBER        = "#f59e0b"
const HEX_COLOUR   = /^#[0-9a-fA-F]{6}$/

// Connects to data-controller="field-places-map"
// Reads place data from a hidden element inside the Turbo Frame so it stays
// in sync when the user searches, filters, or sorts the list.
export default class extends Controller {
  // lazy: do not create the Mapbox map until the frame has places to plot (Activity day map,
  // card #4070), so an empty day never loads a map.
  // viewedPosition (card #4909, Field Signal viewer mode): the Tasker's last known
  // position, sent from the server (Field::TodayController#viewed_user_position) --
  // { lat, lng, at, source }. Only present when a Mission Controller is observing
  // another Tasker (?viewed_user_id=X); absent entirely for a Tasker's own map.
  static values = { frame: String, showRoute: Boolean, lazy: Boolean, viewedPosition: Object }
  // Optional (card #4070): "canvas" is the element the map draws into (default: this element) and
  // "fallback" is a compact message with a retry, shown only when the map was tried and failed.
  static targets = ["fallback", "canvas"]

  connect() {
    this._markers = []

    // Re-render markers when the Turbo Frame updates (search / filter / sort). A lazy map is
    // created here the first time the frame has something to plot.
    this._frameLoadHandler = () => {
      if (this.map) {
        this._refreshMarkers()
      } else if (!this.lazyValue || this._readPlaces().length > 0) {
        this._initMap()
      }
    }
    document.getElementById(this.frameValue)?.addEventListener("turbo:frame-load", this._frameLoadHandler)

    // Resize when switching from list view to map view
    this._resizeHandler = () => {
      if (!this.map) return
      this.map.resize()
      // Re-fit markers if the user hasn't interacted yet -- fitBounds is a no-op
      // when the map container is hidden (0×0), so we redo it on first reveal.
      if (!this._userInteracted) {
        requestAnimationFrame(() => this._fitToMarkers(this._readPlaces()))
      }
      // Trigger geolocation the first time the map becomes visible (card
      // #4833 -- only when there isn't already a fix under 10 minutes old,
      // and never when the Tasker has already said no). Card #4909 -- also
      // never in viewer mode, see the "load" handler in _initMap.
      if (!this._geolocated && !this._viewedPosition) {
        this._maybeGeolocate()
        this._geolocated = true
      }
    }
    window.addEventListener("field-places-map:resize", this._resizeHandler)

    // Re-draw route when Today tab sort order changes
    this._reorderHandler = () => {
      if (this.showRouteValue) this._drawRoute(this._readPlaces())
    }
    window.addEventListener("field-today-sort:reordered", this._reorderHandler)

    if (!this.lazyValue || this._readPlaces().length > 0) this._initMap()
  }

  // Try again after a failure: drop the broken map and build a fresh one.
  retry() {
    this._hideFallback()
    this._clearMarkers()
    this.map?.remove()
    this.map = null
    this._viewedPositionMarker = null
    this._initMap()
  }

  _initMap() {
    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!token || typeof mapboxgl === "undefined") {
      this._showFallback()
      return
    }

    mapboxgl.accessToken = token

    // Card #4909 -- Field Signal viewer mode: centre on the observed Tasker's
    // last known position instead of the viewer's own geolocation/localStorage
    // fix. _userLocation() below checks this first, so every other place that
    // reads "where am I" (fit-to-bounds, the Directions link origin) picks it
    // up too without a second code path.
    this._viewedPosition = this._readViewedPosition()

    const userLoc = this._userLocation()
    const center  = userLoc ? [userLoc.lng, userLoc.lat] : [153.025, -27.47]

    this.map = new mapboxgl.Map({
      container: this._container,
      style: "mapbox://styles/mapbox/streets-v12",
      center,
      zoom: 11
    })

    // The map fell back to the Brisbane default center -- if that's specifically
    // because the tasker denied geolocation permission (not just "no fix yet"),
    // explain it rather than silently centering somewhere unexpected (card #1900).
    if (!userLoc && this._locationDeniedRecently()) this._insertLocationDeniedHint()

    // Style or tile failure before the map first loads: say so rather than leave a blank box.
    this.map.on("error", () => { if (!this.map?.loaded()) this._showFallback() })
    this.map.on("load", () => this._hideFallback())

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right")
    this.map.addControl(new mapboxgl.FullscreenControl(), "top-right")

    this._geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true
    })
    this.map.addControl(this._geolocate, "top-right")

    this._geolocated    = false
    this._userInteracted = false
    this._suppressNextGeolocateInteraction = false

    // Stop auto-fitting once the user manually pans or pinches the map.
    // originalEvent is only present on user-initiated gestures, not programmatic
    // animations like fitBounds, so this won't short-circuit the initial fit.
    this.map.on("dragstart", e => { if (e.originalEvent) this._userInteracted = true })
    this.map.on("zoomstart", e => { if (e.originalEvent) this._userInteracted = true })

    // Compass/geolocate button tapped by the user -- treat as an interaction so
    // auto-fitting doesn't snap the view back out to all markers afterwards.
    // Suppress the flag for the programmatic trigger() on initial load.
    this._geolocate.on("trackuserlocationstart", () => {
      if (this._suppressNextGeolocateInteraction) {
        this._suppressNextGeolocateInteraction = false
        return
      }
      this._userInteracted = true
    })

    // When the GeolocateControl gets a real position, just store it.
    // The GeolocateControl already handles panning to the user's location.
    // We no longer call _fitToMarkers here -- that would zoom out to fit all
    // markers, which is the opposite of what the user wanted when tapping the compass.
    this._geolocate.on("geolocate", (e) => {
      const pos = { lat: e.coords.latitude, lng: e.coords.longitude, accuracy: e.coords.accuracy, timestamp: Date.now() }
      localStorage.setItem(LOCATION_KEY, JSON.stringify(pos))
    })

    // Add a custom "fit all places" control (4-arrows expand icon).
    // Clicking it re-enables auto-fitting and immediately fits all markers.
    this._fitAllControl = this._buildFitAllControl()
    this.map.addControl(this._fitAllControl, "top-right")

    this.map.on("load", () => {
      this._refreshMarkers()
      this._addViewedPositionMarker()
      // Only trigger geolocation if the map is already visible on load
      // (i.e. user had map view saved in localStorage). Card #4909 -- never
      // for a viewer-mode map: the iframe sandbox has no allow=geolocation
      // anyway, and the centre is already the Tasker's own last known fix.
      if (this._container.offsetParent !== null && !this._viewedPosition) {
        this._suppressNextGeolocateInteraction = true
        this._maybeGeolocate()
        this._geolocated = true
      }
    })
  }

  // Card #4833 -- a fix under 10 minutes old is reused (the map is already
  // centred on it via _userLocation() in _initMap, nothing more to do), and a
  // remembered denial is respected rather than re-prompting. Only asks the
  // browser when neither applies.
  _maybeGeolocate() {
    if (hasFreshFix() || isDeniedLocally()) return
    this._geolocate.trigger()
  }

  disconnect() {
    const frame = document.getElementById(this.frameValue)
    frame?.removeEventListener("turbo:frame-load", this._frameLoadHandler)
    window.removeEventListener("field-places-map:resize", this._resizeHandler)
    window.removeEventListener("field-today-sort:reordered", this._reorderHandler)
    this._clearMarkers()
    this._legend?.remove()
    this._legend = null
    this._viewedPositionMarker?.remove()
    this._viewedPositionMarker = null
    this.map?.remove()
    this.map = null
  }

  // -- private --

  get _container() {
    return this.hasCanvasTarget ? this.canvasTarget : this.element
  }

  _showFallback() {
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove("hidden")
    if (this.hasCanvasTarget) this.canvasTarget.classList.add("hidden")
  }

  _hideFallback() {
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
    if (this.hasCanvasTarget) this.canvasTarget.classList.remove("hidden")
  }

  _refreshMarkers() {
    this._clearMarkers()
    // The Activity day map re-renders its data element with "show order" on or off (card #4070).
    const dataEl = document.getElementById(`${this.frameValue}-map-places`)
    if (dataEl && "showOrder" in dataEl.dataset) this.showRouteValue = dataEl.dataset.showOrder === "true"
    const places = this._readPlaces()
    places.forEach(place => this._addMarker(place))
    this._renderLegend(places)
    if (this.showRouteValue) this._drawRoute(places)
    this._fitToMarkers(places)
  }

  _clearMarkers() {
    this._markers.forEach(m => m.remove())
    this._markers = []
    this._clearRoute()
  }

  _drawRoute(places) {
    if (!this.map?.isStyleLoaded()) {
      this.map.once("idle", () => this._drawRoute(places))
      return
    }

    // Visit order (Activity day map): numbered stops only, in order; the main base is not a stop.
    const stops = places.some(p => p.order)
      ? places.filter(p => p.order).sort((a, b) => a.order - b.order)
      : places
    const coords = stops.filter(p => p.lat && p.lng).map(p => [p.lng, p.lat])
    if (coords.length < 2) return

    const geojson = {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "LineString", coordinates: coords } }]
    }

    if (this.map.getSource("today-route")) {
      this.map.getSource("today-route").setData(geojson)
    } else {
      this.map.addSource("today-route", { type: "geojson", data: geojson })
      this.map.addLayer({
        id: "today-route-line",
        type: "line",
        source: "today-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": SAPPHIRE, "line-width": 2, "line-dasharray": [2, 4] }
      }, this._firstSymbolLayer())
    }
  }

  _clearRoute() {
    if (!this.map) return
    if (this.map.getLayer("today-route-line")) this.map.removeLayer("today-route-line")
    if (this.map.getSource("today-route"))     this.map.removeSource("today-route")
  }

  _firstSymbolLayer() {
    // Insert route line below text labels so it doesn't obscure place names
    const layers = this.map.getStyle()?.layers || []
    const sym = layers.find(l => l.type === "symbol")
    return sym?.id
  }

  _addMarker(place) {
    if (!place.lat || !place.lng) return

    const el = document.createElement("div")
    // Numbered stop when "show order" is on (Activity day map), otherwise a plain dot.
    const numbered = this.showRouteValue && place.order
    el.style.cssText = `
      width: ${numbered ? 24 : 14}px; height: ${numbered ? 24 : 14}px;
      background: ${this._pinColour(place)};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 4px rgba(0,0,0,0.35);
      cursor: pointer;
      ${numbered ? "display:flex;align-items:center;justify-content:center;color:white;font-size:12px;font-weight:700;line-height:1;" : ""}
    `
    if (numbered) el.textContent = String(place.order)

    const popup = new mapboxgl.Popup({
      offset: 12,
      closeButton: false,
      maxWidth: "270px"
    }).setHTML(this._popupHtml(place))

    // Wire up close button + schedule form each time the popup opens
    popup.on("open", () => {
      const closeBtn = popup.getElement()?.querySelector("[data-popup-close]")
      if (closeBtn) closeBtn.addEventListener("click", () => popup.remove(), { once: true })
    })

    if (place.schedule_url) {
      popup.on("open", () => {
        const form = popup.getElement()?.querySelector("form[data-schedule]")
        if (!form) return
        form.addEventListener("submit", async (e) => {
          e.preventDefault()
          const dateInput = form.querySelector('input[name="visit_date"]')
          const dateVal   = dateInput?.value || ""
          const csrf      = document.querySelector('meta[name="csrf-token"]')?.content
          const body      = new URLSearchParams({ visit_date: dateVal, authenticity_token: csrf })

          const btn = form.querySelector("button[type=submit]")
          btn.textContent = "Saving…"
          btn.disabled = true

          try {
            await fetch(place.schedule_url, {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "text/vnd.turbo-stream.html" },
              body
            })
            const display = popup.getElement()?.querySelector("[data-scheduled-display]")
            if (display) {
              display.textContent = dateVal ? `Next visit: ${this._formatDate(dateVal)}` : "No visit scheduled"
              display.style.color = dateVal ? "#003d68" : "#94a3b8"
            }
            btn.textContent = "Saved ✓"
            setTimeout(() => { btn.textContent = "Schedule"; btn.disabled = false }, 2000)
          } catch {
            btn.textContent = "Schedule"; btn.disabled = false
          }
        }, { once: true })
      })
    }

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat([place.lng, place.lat])
      .setPopup(popup)
      .addTo(this.map)

    this._markers.push(marker)
  }

  _popupHtml(place) {
    const userLoc = this._userLocation()
    const origin  = userLoc ? `&origin=${userLoc.lat},${userLoc.lng}` : ""
    const dest    = `${place.lat},${place.lng}`
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest}${origin}`

    const priorityBadge = place.priority
      ? `<span style="font-size:11px;font-weight:700;color:${place.home_base ? AMBER : SAPPHIRE};display:block;margin-bottom:2px">#${place.priority}</span>`
      : ""

    const address = place.address
      ? `<span style="font-size:11px;color:#64748b;display:block;margin-top:3px">${place.address}</span>`
      : ""

    const subtitle = place.subtitle
      ? `<span style="font-size:11px;color:#94a3b8;display:block;margin-top:1px">${place.subtitle}</span>`
      : ""

    const retailer = place.retailer
      ? `<span style="font-size:11px;font-weight:600;color:#475569;display:block;margin-top:2px">${this._escape(place.retailer)}</span>`
      : ""

    // Missions done (or scheduled) here on the selected day (Activity day map).
    const missionRows = (place.missions || []).map(m => `
      <li style="display:flex;gap:6px;justify-content:space-between;font-size:11px;color:#334155;padding:2px 0">
        <span style="min-width:0">${this._escape(m.name)}</span>
        <span style="flex-shrink:0;color:#64748b">${this._escape(m.status === "done" ? m.time : `${m.status === "started" ? "Started" : "Scheduled"} ${m.time}`)}</span>
      </li>`).join("")
    const missionList = missionRows ? `<ul style="list-style:none;margin:6px 0 0;padding:6px 0 0;border-top:1px solid #f1f5f9">${missionRows}</ul>` : ""
    const stopNumber = place.order && this.showRouteValue
      ? `<span style="font-size:11px;font-weight:700;color:${SAPPHIRE};display:block;margin-bottom:2px">Stop ${place.order}</span>`
      : ""

    const csrf    = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const total  = (place.todo || 0) + (place.scheduled || 0) + (place.started || 0) + (place.ended || 0)
    const progressHtml = total > 0 ? this._progressBar(place, total) : ""

    const scheduledText = place.scheduled_date
      ? `Next visit: ${this._formatDate(place.scheduled_date)}`
      : "No visit scheduled"
    const scheduledColour = place.scheduled_date ? "#003d68" : "#94a3b8"

    const scheduleSection = place.schedule_url ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #f1f5f9">
        <span data-scheduled-display
              style="font-size:11px;font-weight:600;color:${scheduledColour};display:block;margin-bottom:6px">
          ${scheduledText}
        </span>
        <form method="post" action="${place.schedule_url}" data-schedule
              style="display:flex;align-items:center;gap:6px">
          <input type="hidden" name="authenticity_token" value="${csrf}">
          <input type="date" name="visit_date" value="${place.scheduled_date || ""}"
                 style="flex:1;font-size:11px;border:1px solid #e2e8f0;border-radius:8px;padding:4px 7px;color:#1e293b;min-width:0">
          <button type="submit"
                  style="font-size:11px;font-weight:600;color:white;background:${SAPPHIRE};padding:5px 10px;border-radius:8px;border:none;cursor:pointer;white-space:nowrap">
            Schedule
          </button>
        </form>
      </div>
    ` : ""

    // Card #4108. Only for places assigned to this Tasker (the server sends pin_url
    // for those alone); opens the place details page with the pin fixer ready.
    const pinLink = place.pin_url ? `
      <a href="${place.pin_url}" data-turbo-frame="_top"
         style="display:block;margin-top:8px;text-align:center;font-size:11px;font-weight:600;color:#64748b;text-decoration:underline">
        Pin in the wrong spot? Fix pin location
      </a>` : ""

    return `
      <div style="font-family:inherit;padding:2px 0;min-width:210px">
        <div style="display:flex;align-items:flex-start;gap:4px;margin-bottom:2px">
          <div style="flex:1;min-width:0">
            ${stopNumber}
            ${priorityBadge}
            <a href="${place.url}" data-turbo-frame="_top"
               style="font-size:13px;font-weight:600;color:#1e293b;text-decoration:none;display:block;line-height:1.3">
              ${place.name}
            </a>
          </div>
          <button data-popup-close
                  style="flex-shrink:0;width:32px;height:32px;display:flex;align-items:center;justify-content:center;background:#f1f5f9;border:none;border-radius:50%;cursor:pointer;color:#64748b;font-size:14px;line-height:1;margin-top:-2px">
            ✕
          </button>
        </div>
        ${retailer}
        ${address}
        ${subtitle}
        ${progressHtml}
        ${missionList}
        ${scheduleSection}
        <div style="display:flex;gap:6px;margin-top:8px">
          <a href="${place.url}" data-turbo-frame="_top"
             style="flex:1;display:inline-flex;align-items:center;justify-content:center;gap:4px;font-size:11px;font-weight:600;color:${SAPPHIRE};background:#f1f5f9;padding:5px 8px;border-radius:20px;text-decoration:none">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
            View place
          </a>
          <a href="${mapsUrl}" target="_blank" rel="noopener"
             style="flex:1;display:inline-flex;align-items:center;justify-content:center;gap:4px;font-size:11px;font-weight:600;color:white;background:${SAPPHIRE};padding:5px 8px;border-radius:20px;text-decoration:none">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            Directions
          </a>
        </div>
        ${pinLink}
      </div>
    `
  }

  // Pin colour (card #4107): home base stays amber, otherwise the retailer's stored
  // colour, otherwise the original sapphire. Only a valid #rrggbb is trusted.
  _pinColour(place) {
    if (place.home_base) return AMBER
    return HEX_COLOUR.test(place.retailer_colour || "") ? place.retailer_colour : SAPPHIRE
  }

  _escape(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Legend of the retailers on the map right now (card #4107). Names sit next to
  // the swatches so colour is never the only cue. Retailers without a colour are
  // grouped under "Other retailers" with the default sapphire pin.
  _renderLegend(places) {
    this._legend?.remove()
    this._legend = null

    const plotted  = places.filter(p => p.lat && p.lng && !p.home_base)
    const coloured = new Map()
    let hasOther = false
    plotted.forEach(p => {
      if (p.retailer && HEX_COLOUR.test(p.retailer_colour || "")) coloured.set(p.retailer, p.retailer_colour)
      else hasOther = true
    })
    if (coloured.size === 0) return

    const entries = [...coloured.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    if (hasOther) entries.push(["Other retailers", SAPPHIRE])
    if (places.some(p => p.lat && p.lng && p.home_base)) entries.push(["Main base", AMBER])

    const rows = entries.map(([name, colour]) => `
      <li class="flex items-center gap-2 py-0.5">
        <span class="inline-block shrink-0 rounded-full border-2 border-white ring-1 ring-slate-400" style="width:14px;height:14px;background:${colour}" aria-hidden="true"></span>
        <span class="truncate">${this._escape(name)}</span>
      </li>`).join("")

    const legend = document.createElement("details")
    legend.setAttribute("data-retailer-legend", "")
    legend.className = "absolute left-2 top-2 z-10 max-w-[60%] rounded-lg border border-slate-200 bg-white/95 text-xs text-slate-800 shadow-sm"
    legend.open = entries.length <= 6
    legend.innerHTML = `
      <summary class="cursor-pointer select-none px-3 py-2 font-semibold">Retailers (${entries.length})</summary>
      <ul class="max-h-40 overflow-y-auto px-3 pb-2">${rows}</ul>`
    this._container.appendChild(legend)
    this._legend = legend
  }

  _progressBar(place, total) {
    const ended    = place.ended || 0
    const donePct  = Math.round((ended / total) * 100)

    return `
      <div style="margin-top:7px">
        <div style="height:4px;border-radius:2px;overflow:hidden;background:#e2e8f0">
          <div style="width:${donePct}%;height:100%;background:#10b981;border-radius:2px;transition:width 0.3s"></div>
        </div>
        <span style="font-size:10px;color:#94a3b8;margin-top:2px;display:block">${ended} of ${total} done</span>
      </div>
    `
  }

  _fitToMarkers(places, userLoc = null) {
    if (this._userInteracted) return
    const pts = places.filter(p => p.lat && p.lng)
    if (pts.length === 0) return

    const bounds = new mapboxgl.LngLatBounds()
    pts.forEach(p => bounds.extend([p.lng, p.lat]))

    const loc = userLoc || this._userLocation()
    if (loc) bounds.extend([loc.lng, loc.lat])

    this.map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 800 })
  }

  _readPlaces() {
    const el = document.getElementById(`${this.frameValue}-map-places`)
    if (!el) return []
    try { return JSON.parse(el.dataset.places) || [] }
    catch { return [] }
  }

  _formatDate(dateStr) {
    if (!dateStr) return ""
    const [y, m, d] = dateStr.split("-").map(Number)
    return new Date(y, m - 1, d).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
  }

  // Card #4909 -- in viewer mode this is the observed Tasker's last known
  // position (server-supplied), never the viewer's own browser/localStorage
  // fix. Every caller of _userLocation() (centering, fit-to-bounds, the
  // Directions link origin) picks this up automatically.
  _userLocation() {
    if (this._viewedPosition) return { lat: this._viewedPosition.lat, lng: this._viewedPosition.lng }
    try { return JSON.parse(localStorage.getItem(LOCATION_KEY)) }
    catch { return null }
  }

  // Card #4909 -- only draw the blue "last known position" pin when the
  // server found a real positioned response (source: "response", with a
  // date to show). The two fallbacks (assigned-places centre, Brisbane) are
  // just a sensible place to centre the map -- not an actual sighting of the
  // Tasker, so no pin or date for either.
  _readViewedPosition() {
    if (!this.hasViewedPositionValue) return null
    const v = this.viewedPositionValue
    if (!v || typeof v.lat !== "number" || typeof v.lng !== "number") return null
    return v
  }

  _addViewedPositionMarker() {
    if (!this._viewedPosition || this._viewedPosition.source !== "response" || !this._viewedPosition.at) return

    const el = document.createElement("div")
    el.style.cssText = `
      width: 18px; height: 18px;
      background: #2563eb;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 4px rgba(0,0,0,0.35);
    `

    const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, maxWidth: "220px" }).setHTML(`
      <div style="font-family:inherit;padding:2px 0;min-width:150px">
        <span style="font-size:12px;font-weight:600;color:#1e293b;display:block">Last known position</span>
        <span style="font-size:11px;color:#64748b;display:block;margin-top:2px">${this._escape(this._formatDateTime(this._viewedPosition.at))}</span>
      </div>
    `)

    this._viewedPositionMarker?.remove()
    this._viewedPositionMarker = new mapboxgl.Marker({ element: el })
      .setLngLat([this._viewedPosition.lng, this._viewedPosition.lat])
      .setPopup(popup)
      .addTo(this.map)
      .togglePopup()
  }

  _formatDateTime(isoStr) {
    const date = new Date(isoStr)
    if (Number.isNaN(date.getTime())) return ""
    return date.toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
  }

  _locationDeniedRecently() {
    return isDeniedLocally()
  }

  // Persistent inline hint explaining the Brisbane-fallback center -- mirrors the
  // amber notice pattern from field_photo_camera_controller.js (card #2578).
  // Positioned bottom-of-container (not top-right) to stay clear of Mapbox's own
  // stacked controls (nav/fullscreen/geolocate/fit-all all live top-right).
  // Guards against double-insertion, though connect() only calls this once.
  _insertLocationDeniedHint() {
    if (this._container.querySelector("[data-geolocation-denied-hint]")) return

    const hint = document.createElement("div")
    hint.setAttribute("data-geolocation-denied-hint", "")
    hint.className = "absolute left-2 right-2 bottom-10 z-10 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 shadow-sm"
    hint.innerHTML = `Location is off for this app — the map can't centre on you. <a href="/field/more" class="underline font-medium">Tap here to fix it</a>`
    this._container.appendChild(hint)
  }

  _buildFitAllControl() {
    const controller = this
    return {
      onAdd(map) {
        this._container = document.createElement("div")
        this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group"

        const btn = document.createElement("button")
        btn.title = "Fit all places"
        btn.style.cssText = "display:flex;align-items:center;justify-content:center;width:29px;height:29px;cursor:pointer;"
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
        </svg>`

        btn.addEventListener("click", () => {
          controller._userInteracted = false
          controller._fitToMarkers(controller._readPlaces())
        })

        this._container.appendChild(btn)
        return this._container
      },
      onRemove() {
        this._container.parentNode?.removeChild(this._container)
      }
    }
  }
}
